import { useMemo } from 'react';
import {
	ActionHeader,
	ActionItemsList,
	ActionItemsWrapper,
	ActionItemsHeader,
	StyledLinkButton,
	EmptyWrapper,
} from './ActionItems.style';

import { ActionItemRowComponent } from './ActionItemRowComponent';
import { LinkButton, type ButtonVariant } from '../../../button';
import type { IconName } from '../../../icon';

import { useConfettiArmedContext } from '../employeeDetailsCard/confettiArmedContext';

export type ActionItem = {
	id?: string;
	text: string;
	target?: number;
	value?: number;
	targettype?: string;
	status?: string;
};

export type ActionItemsProps = {
	title?: string;
	link?: string;
	linkText?: string;
	useDynamicColors?: boolean;
	inline?: boolean;
	actionItems?: ActionItem[] | null;

	emptyDataText?: string;
	emptyDataButton?: {
		text: string;
		type: ButtonVariant;
		icon?: IconName;
		link: string;
	};

	/**
	 * Optional override. If not provided, ActionItems uses EmployeeDetailsCard context.
	 */
	confettiArmed?: boolean;
};

export const ActionItems = ({
	title,
	link,
	linkText,
	useDynamicColors,
	inline = true,
	actionItems,
	emptyDataText,
	emptyDataButton,
	confettiArmed: confettiArmedProp,
}: ActionItemsProps) => {
	const contextArmed = useConfettiArmedContext();
	const confettiArmed = confettiArmedProp ?? contextArmed;

	const items = useMemo(() => actionItems ?? [], [actionItems]);
	const isEmpty = items.length === 0;

	const completedSequenceIndexByKey = useMemo(() => {
		const completedKeys: string[] = [];

		for (const item of items) {
			if (item.target !== undefined && item.value !== undefined && item.target > 0) {
				if (item.value >= item.target) completedKeys.push(item.id ?? item.text);
			}
		}

		const map: Record<string, number> = {};
		completedKeys.forEach((k, idx) => {
			map[k] = idx;
		});
		return map;
	}, [items]);

	return (
		<ActionItemsWrapper $inline={inline}>
			{inline ? (
				<ActionItemsHeader>
					{title ? <ActionHeader>{title}</ActionHeader> : null}

					{link && linkText && !isEmpty ? (
						<StyledLinkButton href={link} label={linkText} variant="link" />
					) : null}
				</ActionItemsHeader>
			) : null}

			<ActionItemsList>
				{isEmpty ? (
					<EmptyWrapper>
						<p>{emptyDataText ?? 'No data'}</p>

						{emptyDataButton ? (
							<LinkButton
								variant={emptyDataButton.type}
								icon={emptyDataButton.icon}
								href={emptyDataButton.link}
								size="small"
							>
								{emptyDataButton.text}
							</LinkButton>
						) : null}
					</EmptyWrapper>
				) : (
					items.map((item, index) => {
						const key = item.id ?? item.text;
						return (
							<div key={`${key}-${index}`}>
								<ActionItemRowComponent
									item={item}
									sequenceIndex={completedSequenceIndexByKey[key] ?? 0}
									confettiArmed={confettiArmed}
									useDynamicColors={useDynamicColors}
									inline={inline}
								/>
							</div>
						);
					})
				)}
			</ActionItemsList>
		</ActionItemsWrapper>
	);
};
